import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { Board, Difficulty, GameState } from "./types";
import { createEmptyBoard, generateUniqueSudokuBoard } from "./sudoku";
import { useCanvasSize, useRefBoard } from "./hooks";
import { deepCopy2DArray } from "./utils";
import Timer from "./components/Timer";

export const START_OFFSET = 1;
export const THICK_LINE_WIDTH = 2;
export const THIN_LINE_WIDTH = 1;

// cell color
const SELECTED_COLOR = "#BBDEFB";
const RELATED_COLOR = "#f0f5fa";
const SAME_NUMBER_COLOR = "#d3dee8";

// number color
const INITIAL_NUMBER_COLOR = "#333";
const ADDED_NUMBER_COLOR = "#0072e3";
const NOTE_COLOR = "#333";

// number font
const NUMBER_FONT = "30px Arial normal";
const NOTE_FONT_NORMAL = "15px Arial";
const NOTE_FONT_BOLD = "bold 15px Arial";

const isDev = process.env.NODE_ENV === 'development';

const DEFAULT_DIFFICULTY = isDev ? Difficulty.dev : Difficulty.easy;


const App = () => {
  const [CANVAS_SIZE, BOARD_SIZE, CELL_SIZE] = useCanvasSize();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [initialBoardRef, updateInitialBoard] = useRefBoard([]);
  const [answerBoardRef, updateAnswerBoard] = useRefBoard([]);

  const [numberBoardRef, updateNumberBoard] = useRefBoard([]);
  const [noteBoardRef, updateNoteBoard] = useRefBoard([]);
  const [selectedCell, setSelectedCell] = useState<number[] | null>(null);

  const [difficulty, setDifficulty] = useState(DEFAULT_DIFFICULTY);
  const [gameState, setGameState] = useState(GameState.playing);
  const [enableNote, setEnableNote] = useState(false);

  const [showChooseDifficulty, setShowChooseDifficuly] = useState(false);

  const [history, setHistory] = useState<string[]>([]);

  const timerRef = useRef<any>();

  useEffect(() => {
    if (CANVAS_SIZE) {
      init(difficulty);
    }
  }, [CANVAS_SIZE]);

  const init = (difficulty: Difficulty) => {
    const [initBoard, solvedBoard] = generateUniqueSudokuBoard(difficulty);
    const noteBoard = createEmptyBoard();

    updateInitialBoard(initBoard);
    updateNumberBoard(initBoard);
    updateNoteBoard(noteBoard);
    updateAnswerBoard(solvedBoard);

    draw(initBoard, noteBoard, null);
    scrollToBoard();
  };

  // canvas operations
  const draw = (
    numberBoard: Board,
    noteBoard: Board,
    selectedCell: number[] | null,
    initialBoard = initialBoardRef.current
  ) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    clearBoard(ctx);

    if (selectedCell) {
      hightlightCells(ctx, numberBoard, selectedCell);
    }
    drawSudokuBoard(ctx);
    drawNumbers(ctx, initialBoard, numberBoard);
    drawNotes(ctx, noteBoard, selectedCell);
  };

  const clearBoard = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(START_OFFSET, START_OFFSET, BOARD_SIZE, BOARD_SIZE);
  };

  // order: 1
  const hightlightCells = (ctx: CanvasRenderingContext2D, displayedBoard: Board, clickedCell: number[]) => {
    const [clickedRow, clickedCol] = clickedCell;

    ctx.fillStyle = RELATED_COLOR;
    ctx.fillRect(START_OFFSET, START_OFFSET + clickedRow * CELL_SIZE, BOARD_SIZE, CELL_SIZE); // same row
    ctx.fillRect(START_OFFSET + clickedCol * CELL_SIZE, START_OFFSET, CELL_SIZE, BOARD_SIZE); // same col
    // same area
    // ctx.fillRect(
    //   CELL_SIZE + Math.floor(clickedCol / 3) * AREA_SIZE,
    //   CELL_SIZE + Math.floor(clickedRow / 3) * AREA_SIZE,
    //   AREA_SIZE,
    //   AREA_SIZE
    // );

    // same number
    ctx.fillStyle = SAME_NUMBER_COLOR;
    const clickedVal = displayedBoard[clickedRow][clickedCol];
    if (clickedVal !== ".") {
      for (let row = 0; row < 9; row++) {
        for (let col = 0; col < 9; col++) {
          if (displayedBoard[row][col] === clickedVal) {
            ctx.fillRect(START_OFFSET + col * CELL_SIZE, START_OFFSET + row * CELL_SIZE, CELL_SIZE, CELL_SIZE);
          }
        }
      }
    }

    // clicked number
    ctx.fillStyle = SELECTED_COLOR;
    ctx.fillRect(START_OFFSET + clickedCol * CELL_SIZE, START_OFFSET + clickedRow * CELL_SIZE, CELL_SIZE, CELL_SIZE);
  };

  // order: 2
  const drawSudokuBoard = (ctx: CanvasRenderingContext2D) => {
    ctx.strokeStyle = "#333";
    ctx.lineWidth = THICK_LINE_WIDTH;
    const [startX, startY] = [START_OFFSET, START_OFFSET];
    const [endX, endY] = [startX + BOARD_SIZE, startY + BOARD_SIZE];
    ctx.strokeRect(startX, startY, BOARD_SIZE, BOARD_SIZE);

    ctx.lineWidth = THIN_LINE_WIDTH;
    for (let i = 1; i < 9; i++) {
      const x = i * CELL_SIZE + startX;
      const y = i * CELL_SIZE + startY;
      if (i % 3 === 0) {
        ctx.lineWidth = THICK_LINE_WIDTH;
      } else {
        ctx.lineWidth = THIN_LINE_WIDTH;
      }

      ctx.beginPath();
      ctx.moveTo(startX, y);
      ctx.lineTo(endX, y);
      ctx.moveTo(x, startY);
      ctx.lineTo(x, endY);
      ctx.stroke();
    }
  };

  // order: 3
  const drawNumbers = (ctx: CanvasRenderingContext2D, initialNumber: Board, addedNumbers: Board) => {
    ctx.font = NUMBER_FONT;
    ctx.fillStyle = ADDED_NUMBER_COLOR;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const getPos = (n: number) => START_OFFSET + n * CELL_SIZE + CELL_SIZE / 2;

    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (initialNumber[row][col] !== ".") {
          ctx.fillStyle = INITIAL_NUMBER_COLOR;
          ctx.fillText(initialNumber[row][col], getPos(col), getPos(row));
        } else if (addedNumbers[row][col] !== ".") {
          ctx.fillStyle = ADDED_NUMBER_COLOR;
          ctx.fillText(addedNumbers[row][col], getPos(col), getPos(row));
        }
      }
    }
  };

  // order: 3
  const drawNotes = (ctx: CanvasRenderingContext2D, noteBoard: Board, selectedCell: number[] | null) => {
    ctx.fillStyle = NOTE_COLOR;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    let selectedVal: string | null = null;
    if (selectedCell) {
      const [row, col] = selectedCell;
      if (numberBoardRef.current[row][col] !== ".") {
        selectedVal = numberBoardRef.current[row][col];
      }
    }

    const noteCellSize = CELL_SIZE / 3;
    const getPos = (n: number, offset: number) =>
      START_OFFSET + n * CELL_SIZE + noteCellSize * offset + noteCellSize / 2;

    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        const notes = noteBoard[row][col];
        for (let i = 0; i < notes.length; i++) {
          const noteVal = Number(notes[i]);
          if (Number.isNaN(noteVal)) continue;
          const offsetX = (noteVal - 1) % 3;
          const offsetY = Math.floor((noteVal - 1) / 3);
          if (notes[i] === selectedVal) {
            ctx.font = NOTE_FONT_BOLD;
          } else {
            ctx.font = NOTE_FONT_NORMAL;
          }
          ctx.fillText(notes[i], getPos(col, offsetX), getPos(row, offsetY));
        }
      }
    }
  };

  // functions
  
  const scrollToBoard = () => {
    setTimeout(() => {
      window.scrollTo({
        top: 150,
        left: 0,
        behavior: "smooth",
      });
    }, 500);
  };

  /**
   * @return [row, col]
   * */
  const getClickedCell = (canvas: HTMLCanvasElement, event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    const rect = canvas.getBoundingClientRect();

    const x = event.clientX - rect.left - START_OFFSET;
    const y = event.clientY - rect.top - START_OFFSET;

    const row = Math.floor(y / CELL_SIZE);
    const col = Math.floor(x / CELL_SIZE);

    return [row, col];
  };

  /**
   * @return [isFull, sameWithAnswer]
   * */
  const getBoardState = (board: Board) => {
    let isFull = true;
    let sameWithAnswer = true;
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (board[row][col] !== answerBoardRef.current[row][col]) {
          sameWithAnswer = false;
        }
        if (board[row][col] === ".") {
          isFull = false;
        }
      }
    }
    return [isFull, sameWithAnswer];
  };

  const isFullBoardValid = (board: Board) => {
    // check row
    for (let row = 0; row < 9; row++) {
      const rowSet = new Set(board[row]);
      if (rowSet.size !== 9) return false;
    }

    // check col
    for (let col = 0; col < 9; col++) {
      const colSet = new Set();
      for (let row = 0; row < 9; row++) {
        colSet.add(board[row][col]);
      }
      if (colSet.size !== 9) return false;
    }

    // check area
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        const areaSet = new Set();
        const x = i * 3;
        const y = j * 3;
        for (let k = 0; k < 3; k++) {
          for (let w = 0; w < 3; w++) {
            const row = x + k;
            const col = y + w;
            areaSet.add(board[row][col]);
          }
        }
        if (areaSet.size !== 9) return false;
      }
    }

    return true;
  };

  const checkGameOver = (board: Board) => {
    // is board full
    const [isFull, sameWithAnswer] = getBoardState(board);

    if (!isFull) return;

    if (sameWithAnswer || isFullBoardValid(board)) {
      // full and correct
      setGameState(GameState.success);
    } else {
      // wrong answer
      // setGameState(GameState.wrong);
      console.log("wrong answer");
    }
  };

  const removeUnnecessaryNotes = (noteBoard: Board, value: string, row: number, col: number) => {
    const areaRow = Math.floor(row / 3) * 3;
    const areaCol = Math.floor(col / 3) * 3;

    for (let i = 0; i < 9; i++) {
      // same row
      if (noteBoard[row][i].includes(value)) {
        noteBoard[row][i] = noteBoard[row][i].replace(value, "");
      }
      // same col
      if (noteBoard[i][col].includes(value)) {
        noteBoard[i][col] = noteBoard[i][col].replace(value, "");
      }
      // same area
      const r = areaRow + Math.floor(i / 3);
      const c = areaCol + (i % 3);
      if (noteBoard[r][c].includes(value)) {
        noteBoard[r][c] = noteBoard[r][c].replace(value, "");
      }
    }
  };

  const addNumberToBoard = (value: string, row: number, col: number) => {
    // remove selected cell notes
    const newNoteBoard = deepCopy2DArray(noteBoardRef.current);
    newNoteBoard[row][col] = "";

    //remove unnecessary notes
    removeUnnecessaryNotes(newNoteBoard, value, row, col);
    updateNoteBoard(newNoteBoard);

    // add number
    const newNumberBoard = deepCopy2DArray(numberBoardRef.current);
    newNumberBoard[row][col] = value;
    updateNumberBoard(newNumberBoard);

    draw(newNumberBoard, newNoteBoard, selectedCell);

    checkGameOver(newNumberBoard);
  };

  const toggleNoteOnBoard = (value: string, row: number, col: number) => {
    // remove current number in cell
    const newNumberBoard = deepCopy2DArray(numberBoardRef.current);
    newNumberBoard[row][col] = ".";
    updateNumberBoard(newNumberBoard);

    // toggle note in cell
    const newNoteBoard = deepCopy2DArray(noteBoardRef.current);

    let currentNote = newNoteBoard[row][col];

    if (currentNote.includes(value)) {
      // remove value in note
      currentNote = currentNote.replace(value, "");
    } else {
      // add value to note
      currentNote += value;
    }

    newNoteBoard[row][col] = currentNote;
    updateNoteBoard(newNoteBoard);

    draw(newNumberBoard, newNoteBoard, selectedCell);
  };

  const showDifficulty = () => {
    switch (difficulty) {
      case Difficulty.easy:
        return "简单";
      case Difficulty.medium:
        return "中等";
      case Difficulty.hard:
        return "困难";
      case Difficulty.insane:
        return "专家";
      case Difficulty.grandpa:
        return "爷爷级";
      default:
        return "";
    }
  };

  const addHistory = () => {
    const arr: string[] = [];
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        const str = numberBoardRef.current[row][col] + "-" + noteBoardRef.current[row][col];
        arr.push(str);
      }
    }
    if (selectedCell) {
      const [row, col] = selectedCell;
      arr.push(`${row}-${col}`);
    }

    const draftHistory = history.slice();
    draftHistory.push(arr.toString());

    setHistory(draftHistory);
  };

  const popHistory = () => {
    if (history.length === 0) return null;

    const draftHistory = history.slice();
    const res = draftHistory.pop();
    setHistory(draftHistory);
    return res;
  };

  // events

  const handlePause = () => {
    if (gameState === GameState.playing) {
      setGameState(GameState.pause);
      const tempBoard = createEmptyBoard();
      draw(tempBoard, tempBoard, null, tempBoard);
    } else if (gameState === GameState.pause) {
      setGameState(GameState.playing);
      draw(numberBoardRef.current, noteBoardRef.current, selectedCell);
    }
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const clickedCell = getClickedCell(canvasRef.current, event);
    const [clickedRow, clickedCol] = clickedCell;

    if (clickedRow < 0 || clickedRow >= 9 || clickedCol < 0 || clickedCol >= 9) {
      // outside board
      setSelectedCell(null);
      draw(numberBoardRef.current, noteBoardRef.current, null);
    } else {
      if (selectedCell) {
        const [selectedRow, selectedCol] = selectedCell;
        if (selectedRow === clickedRow && selectedCol === clickedCol) {
          // deselect
          setSelectedCell(null);
          draw(numberBoardRef.current, noteBoardRef.current, null);
        } else {
          setSelectedCell(clickedCell);
          draw(numberBoardRef.current, noteBoardRef.current, clickedCell);
        }
      } else {
        setSelectedCell(clickedCell);
        draw(numberBoardRef.current, noteBoardRef.current, clickedCell);
      }
    }
  };

  const handleNumPadClick = (value: string) => {
    if (!selectedCell) return;

    const [row, col] = selectedCell;
    if (initialBoardRef.current[row][col] !== ".") return;

    addHistory();

    if (enableNote) {
      // add note to board
      toggleNoteOnBoard(value, row, col);
    } else {
      addNumberToBoard(value, row, col);
    }
  };

  const handleRemoveCell = () => {
    if (!selectedCell) return;

    addHistory();

    const [row, col] = selectedCell;

    const newNumberBoard = deepCopy2DArray(numberBoardRef.current);
    const newNoteBoard = deepCopy2DArray(noteBoardRef.current);

    newNumberBoard[row][col] = ".";
    newNoteBoard[row][col] = "";

    updateNumberBoard(newNumberBoard);
    updateNoteBoard(newNoteBoard);

    draw(newNumberBoard, newNoteBoard, selectedCell);
  };

  const handleReset = () => {
    const newNumberBoard = deepCopy2DArray(initialBoardRef.current);
    const newNoteBoard = createEmptyBoard();
    updateNumberBoard(newNoteBoard);
    updateNoteBoard(newNoteBoard);
    draw(newNumberBoard, newNoteBoard, null);
  };

  const onClickNewGame = () => {
    setShowChooseDifficuly(true);
  };

  const handleStartNewGame = (difficulty: Difficulty) => {
    setGameState(GameState.playing);
    setDifficulty(difficulty);
    setSelectedCell(null);
    setShowChooseDifficuly(false);
    setHistory([]);
    init(difficulty);
    timerRef.current?.setGameSeconds(0);
  };

  const handleUndo = () => {
    const h = popHistory();
    if (!h) return;

    const numberBoard = createEmptyBoard();
    const noteBoard = createEmptyBoard();

    const arr = h.split(",");
    for (let i = 0; i < arr.length - 1; i++) {
      const str = arr[i];
      const [num, note] = str.split("-");
      const [row, col] = [Math.floor(i / 9), i % 9];
      numberBoard[row][col] = num;
      noteBoard[row][col] = note;
    }

    let selectedCell: number[] | null = null;
    const cellStr = arr[arr.length - 1];
    if (cellStr) {
      const [rowStr, colStr] = cellStr.split("-");
      const row = Number(rowStr);
      const col = Number(colStr);
      if (!Number.isNaN(row) && !Number.isNaN(col)) {
        selectedCell = [row, col];
      }
    }
    updateNumberBoard(numberBoard);
    updateNoteBoard(noteBoard);
    draw(numberBoard, noteBoard, selectedCell);
  };

  return (
    <div className="App">
      <header>Suduko</header>

      <main>
        <div className="game-wrapper">
          <div className="blank">
            <button onClick={onClickNewGame}>新游戏</button>
          </div>

          <div className="game-status-wrapper">
            <span>难度: {showDifficulty()}</span>
            <span>总局数: 1</span>
            <Timer gameState={gameState} onClick={handlePause} ref={timerRef} />
          </div>

          <div id="canvas-wrapper" className="canvas-wrapper">
            {CANVAS_SIZE !== null && (
              <canvas
                ref={canvasRef}
                id="board-canvas"
                height={CANVAS_SIZE}
                width={CANVAS_SIZE}
                onClick={handleCanvasClick}
                // onMouseMove={handleCanvasMouseMove}
              />
            )}
          </div>

          <div className="controls-wrapper">
            <div className="num-pad">
              <button onClick={() => handleNumPadClick("1")}>1</button>
              <button onClick={() => handleNumPadClick("2")}>2</button>
              <button onClick={() => handleNumPadClick("3")}>3</button>
              <button onClick={() => handleNumPadClick("4")}>4</button>
              <button onClick={() => handleNumPadClick("5")}>5</button>
              <button onClick={() => handleNumPadClick("6")}>6</button>
              <button onClick={() => handleNumPadClick("7")}>7</button>
              <button onClick={() => handleNumPadClick("8")}>8</button>
              <button onClick={() => handleNumPadClick("9")}>9</button>
            </div>

            <div className="controls">
              <button className="controls-item" onClick={handleReset}>
                复原
              </button>
              <button className="controls-item" onClick={handleUndo} disabled={history.length === 0}>
                撤销
              </button>
              <button className="controls-item" onClick={handleRemoveCell}>
                擦除
              </button>
              <button
                className={`controls-item ${enableNote ? "enabled" : ""}`}
                onClick={() => setEnableNote((v) => !v)}
              >
                备注
              </button>
              <button className="controls-item" disabled>
                提示
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* popups */}
      {gameState === GameState.success && (
        <div className="game-over-wrapper">
          <div className="game-over-popup">
            <div>成功</div>
            <button onClick={onClickNewGame}>新游戏</button>
          </div>
        </div>
      )}

      {gameState === GameState.pause && (
        <div className="game-over-wrapper">
          <div className="game-over-popup">
            <div>暂停</div>
            <button onClick={handlePause}>继续</button>
          </div>
        </div>
      )}

      {showChooseDifficulty && (
        <div className="game-over-wrapper">
          <div className="game-over-popup">
            <div>选择难度</div>
            {isDev ? <div onClick={() => handleStartNewGame(Difficulty.dev)}>开发者</div> : null}
            <div onClick={() => handleStartNewGame(Difficulty.easy)}>简单</div>
            <div onClick={() => handleStartNewGame(Difficulty.medium)}>中等</div>
            <div onClick={() => handleStartNewGame(Difficulty.hard)}>困难</div>
            <div onClick={() => handleStartNewGame(Difficulty.insane)}>专家</div>
            <div onClick={() => handleStartNewGame(Difficulty.grandpa)}>爷爷级</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
